import React from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { IColumnConfig } from '@/types/admin/dataTable';
import { IFundingTableRow, FundingJournalData } from '@/types/admin/funding';
import { MEMO_LEDGER, MEMO_FEE, MEMO_TOTAL } from '@/utils/admin/fundingUtils';
import { ADMIN_COLORS } from '@/constants/admin';
import { FundingSingleAction } from './FundingSingleAction';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { JournalEntryCell } from './JournalEntryCell';
import { formatUtcDate } from '@/utils/admin/date';


// Column configuration for funding data table (matching admin-web exactly)
export const fundingTableColumns = (
  onReprocessComplete?: (updatedFunding: IFundingTableRow) => void,
  options?: { isDesktop?: boolean }
): IColumnConfig<IFundingTableRow>[] => [
  {
    key: 'property',
    label: 'PROPERTY',
    sortable: false,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    filterType: 'multiSelect',
    filterOptions: [], // Will be populated dynamically
    renderCell: (value: string) => (
      <Text style={styles.propertyText} numberOfLines={2}>
        {value}
      </Text>
    ),
  },
  {
    key: 'merchantId',
    label: 'MERCHANT ID',
    sortable: false,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    filterType: 'multiSelect',
    filterOptions: [], // Will be populated dynamically
    renderCell: (value: string) => (
      <Text style={styles.merchantText} numberOfLines={1}>
        {value}
      </Text>
    ),
  },
  {
    key: 'fundingDate',
    label: 'FUNDING DATE',
    sortable: false,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'date',
    filterType: 'date',
    renderCell: (value: Date) => (
      <Text style={styles.dateText}>{formatUtcDate(value)}</Text>
    ),
  },
  {
    key: 'lastStatus',
    label: 'LAST STATUS',
    sortable: false,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    filterType: 'text',
    renderCell: (value: string) => (
      options?.isDesktop ? (
        <DesktopOverflowText text={value} title="Last Status" numberOfLines={2} />
      ) : (
        <Text style={styles.statusText}>
          {value}
        </Text>
      )
    ),
  },
  {
    key: 'funding_amount',
    label: 'LEDGER',
    sortable: false,
    filterable: false,
    visible: true,
    mobileVisible: true,
    sortType: 'number',
    renderCell: (value: FundingJournalData | null) => (
      <>
        {!(options && options.isDesktop) && (
          <Text style={styles.statusText}>
            Funding: 
          </Text>
        )}
      <JournalEntryCell
        journalData={value}
        memos={[...MEMO_LEDGER]}
        />
      </>
    ),
  },
  {
    key: 'fee_amount',
    label: 'FEE',
    sortable: false,
    filterable: false,
    visible: true,
    mobileVisible: true,
    sortType: 'number',
    renderCell: (value: FundingJournalData | null) => (
      <>
        {!(options && options.isDesktop) && (
          <Text style={styles.statusText}>
            Fee: 
          </Text>
        )}
        <JournalEntryCell
          journalData={value}
          memos={[...MEMO_FEE]}
        />
      </>
    ),
  },
  {
    key: 'net_amount',
    label: 'TOTAL FUNDED',
    sortable: false,
    filterable: false,
    visible: true,
    mobileVisible: true,
    sortType: 'number',
    renderCell: (value: FundingJournalData | null) => (
      <>
        {!(options && options.isDesktop) && (
          <Text style={styles.statusText}>
            Total: 
          </Text>
        )}
        <JournalEntryCell
          journalData={value}
          memos={[...MEMO_TOTAL]}
        />
      </>
    ),
  },
  {
    key: 'actions',
    label: 'ACTIONS',
    sortable: false,
    filterable: false,
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    renderCell: (value: number, row: IFundingTableRow) => (
      <FundingSingleAction
        funding={row}
        onReprocessComplete={onReprocessComplete || (() => {})}
      />
    ),
  },
];

const styles = StyleSheet.create({
  propertyText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  merchantText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'right',
  },
  dateText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
});
